/**
 * Dialog des notifications in-app — style sobre type iOS :
 * texte uniquement, pas de pastilles ni de bordures colorées.
 * Affiche aussi les 3 derniers appels du journal (pro) avec un bouton
 * « Journal » qui redirige vers la page complète. Un bip court retentit
 * à l'arrivée d'une nouvelle notification non lue.
 */
import React, { useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Divider,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
} from '@mui/material';
import { useSelector } from 'react-redux';
import {
  useListNotificationsQuery,
  useMarkNotificationReadMutation,
  useListCallsQuery,
} from '../../lib/api/baseApi';
import type { RootState } from '../../app/store';
import type { AppNotification } from '../../types';

interface NotificationsDialogProps {
  open: boolean;
  onClose: () => void;
  onNavigateToCalls: () => void;
}

/** Bip court via Web Audio API — aucun asset, joué après interaction utilisateur. */
function playNotificationSound(): void {
  if (typeof window === 'undefined') return;
  try {
    const Ctx =
      window.AudioContext ??
      (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.value = 880;
    gain.gain.setValueAtTime(0.08, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.25);
    osc.connect(gain).connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + 0.25);
    osc.onended = () => void ctx.close();
  } catch {
    // Le son est best-effort : jamais bloquant.
  }
}

/**
 * Sonne uniquement quand le nombre de notifications non lues augmente
 * (nouvelle notification reçue), jamais au premier chargement.
 */
export function useNotificationSound(unreadCount: number, enabled: boolean): void {
  const prevCount = useRef<number | null>(null);

  useEffect(() => {
    if (!enabled) return;
    const prev = prevCount.current;
    prevCount.current = unreadCount;
    if (prev !== null && unreadCount > prev) playNotificationSound();
  }, [unreadCount, enabled]);
}

function relativeTime(iso: string): string {
  const then = new Date(iso.includes('T') ? iso : `${iso.replace(' ', 'T')}Z`).getTime();
  if (!Number.isFinite(then)) return '';
  const diffMin = Math.max(0, Math.round((Date.now() - then) / 60000));
  if (diffMin < 1) return "À l'instant";
  if (diffMin < 60) return `Il y a ${diffMin} min`;
  const hours = Math.round(diffMin / 60);
  if (hours < 24) return `Il y a ${hours} h`;
  return `Il y a ${Math.round(hours / 24)} j`;
}

export const NotificationsDialog: React.FC<NotificationsDialogProps> = ({
  open,
  onClose,
  onNavigateToCalls,
}) => {
  const sessionUser = useSelector((s: RootState) => s.session.user);
  const isPro = sessionUser?.role === 'professional_owner' || sessionUser?.role === 'professional_agent';
  const { data: notifications = [], isLoading } = useListNotificationsQuery(undefined, {
    skip: !open,
    pollingInterval: 30_000,
  });
  const { data: calls = [] } = useListCallsQuery({ limit: 3 }, { skip: !open || !isPro });
  const [markRead] = useMarkNotificationReadMutation();

  const unread = notifications.filter((n) => !n.read);
  useNotificationSound(unread.length, true);

  const handleClick = (n: AppNotification) => {
    if (!n.read) void markRead(n.id);
    onClose();
    onNavigateToCalls();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 700, px: 2, py: 1.5 }}>
        Notifications{unread.length > 0 ? ` (${unread.length})` : ''}
      </DialogTitle>
      <DialogContent dividers sx={{ px: 0 }}>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={26} />
          </Box>
        ) : notifications.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
            Aucune notification pour le moment.
          </Typography>
        ) : (
          <List disablePadding>
            {notifications.map((n, index) => (
              <ListItem
                key={n.id}
                component="button"
                onClick={() => handleClick(n)}
                divider={index < notifications.length - 1}
                sx={{
                  display: 'block',
                  textAlign: 'left',
                  width: '100%',
                  border: 0,
                  bgcolor: 'transparent',
                  fontWeight: n.read ? 400 : 700,
                  '&:hover': { bgcolor: 'transparent' },
                }}
              >
                <ListItemText
                  primary={n.title}
                  secondary={`${n.body} — ${relativeTime(n.createdAt)}`}
                  slotProps={{
                    primary: { sx: { fontWeight: n.read ? 400 : 700, fontSize: '0.95rem' } },
                    secondary: { sx: { color: 'text.secondary', fontSize: '0.82rem' } },
                  }}
                />
              </ListItem>
            ))}
          </List>
        )}

        {isPro && calls.length > 0 && (
          <Box sx={{ px: 2, mt: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
              Derniers appels
            </Typography>
            <Divider />
            {calls.slice(0, 3).map((c) => (
              <Typography key={c.id} variant="body2" color="text.secondary" sx={{ py: 0.5 }} noWrap>
                {c.callerName ?? 'Client'} — {c.timestamp ?? ''}
              </Typography>
            ))}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 2, py: 1 }}>
        <Button onClick={onClose}>Fermer</Button>
        <Button variant="text" onClick={() => { onClose(); onNavigateToCalls(); }}>
          Journal
        </Button>
      </DialogActions>
    </Dialog>
  );
};
